import { unlock } from 'tua-body-scroll-lock';

const selectors = {
  IMAGE_MAIN_SELECTOR: '[data-selector="image-main"]',
  IMAGE_THUMB_SELECTOR: '[data-selector="image-thumb"]',
  CLOSE_MODAL_SELECTOR: '[data-selector="close"]',
  GALLERY_MAIN_SELECTOR: '[data-selector="gallery-main"]',
};

const classes = {
  ACTIVE: 'is-active',
  HIDDEN_MODAL: 'modal-zoom-gallery--hidden',
};

export class ModalZoomGallery extends HTMLElement {
  connectedCallback() {
    this.galleryMainEl = this.querySelector(selectors.GALLERY_MAIN_SELECTOR);
    this.imageMainEls = this.querySelectorAll(selectors.IMAGE_MAIN_SELECTOR);
    this.imageThumbEls = this.querySelectorAll(selectors.IMAGE_THUMB_SELECTOR);
    this.closeModalEl = this.querySelector(selectors.CLOSE_MODAL_SELECTOR);
    this.abort = new AbortController();
    this.isScrolling = false;

    this.imageThumbEls.forEach((thumb) => {
      thumb.addEventListener(
        'click',
        (/** @type {Event} */ event) => this.handleImageThumbClick(event),
        { signal: this.abort?.signal },
      );
    });

    this.closeModalEl?.addEventListener('click', this.handleCloseModal, {
      signal: this.abort?.signal,
    });

    this.galleryMainEl?.addEventListener('click', this.handleCloseModal, {
      signal: this.abort?.signal,
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.handleCloseModal();
      }
    });

    this.addEventListener('scrollend', () => {
      this.isScrolling = false;
    });

    this.syncThumbImageWithScroll();
  }

  syncThumbImageWithScroll = () => {
    const observerOptions = {
      root: this,
      threshold: 0.1,
      rootMargin: '0px',
    };

    this.observer = new IntersectionObserver((entries) => {
      // Skip if scrolling from thumb click
      if (this.isScrolling) return;

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const targetId = /** @type {HTMLElement} */ (entry.target).dataset.target;
        const thumbEl = this.imageThumbEls
          ? Array.from(this.imageThumbEls).find(
              (thumb) => /** @type {HTMLElement} */ (thumb).dataset.target === targetId,
            )
          : null;

        if (!thumbEl) return;

        this.removeActiveImage(this.imageThumbEls);
        thumbEl.classList.add(classes.ACTIVE);
        thumbEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }, observerOptions);

    this.imageMainEls?.forEach((imageMain) => {
      this.observer?.observe(imageMain);
    });
  };

  /**
   * @param {Event} event
   */
  handleImageThumbClick = (event) => {
    const currentTarget = /** @type {HTMLElement} */ (event.currentTarget);
    const targetId = currentTarget.dataset?.target;
    const imageMainEl = this.imageMainEls
      ? Array.from(this.imageMainEls).find(
          (item) => /** @type {HTMLElement} */ (item).dataset.target === targetId,
        )
      : null;

    if (!imageMainEl || !this.imageThumbEls?.length) return;

    this.isScrolling = true;

    this.removeActiveImage(this.imageThumbEls);
    currentTarget.classList.add(classes.ACTIVE);
    imageMainEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  removeActiveImage = (items) => {
    items.forEach((/** @type {HTMLElement} */ item) => {
      item.classList.remove(classes.ACTIVE);
    });
  };

  handleCloseModal = () => {
    this.classList.add(classes.HIDDEN_MODAL);
    unlock(/** @type {HTMLElement} */ (this));
  };

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
  }
}

if (!customElements.get('modal-zoom-gallery')) {
  customElements.define('modal-zoom-gallery', ModalZoomGallery);
}
